import React, { useCallback, useReducer, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Divider,
    Typography,
} from '@mui/material';
import { ValidDataService } from '../../../../service/validDataService';
import { isLocalDev } from '../../../../utils/environment';
import useAbortController from '../../../../hooks/useAbortController';
import SelectableTable from '../../../enhancedTable/SelectableTable';
import { Cancel, CheckCircle } from '@mui/icons-material';

export default function TreatmentSelectDialog({ open, onClose, onSelect}) {
    const [signal, abortRequest] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    useEffect(() => {
        // Cleanup function that runs when component unmounts
        // or when signal changes
        return () => {
            console.log('Cleaning up');
            if (!open) {
                abortRequest();
            }
        };
    }, []); // Empty dependency array = only on unmount

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Price',
                accessor: 'listPrice',
            },
            {
                Header: 'Is Taxable',
                accessor: 'isTaxable',
                Cell: ({ value }) => (
                    <Typography variant="body2" color={value ? 'green' : 'red'}>
                        {value ? <CheckCircle /> : <Cancel />}
                    </Typography>
                ),
            },
            {
                Header: 'Tax %',
                accessor: 'defaultTaxPercentage',
                Cell: ({ value }) => (
                    <Typography variant="body2">
                        {value}%
                    </Typography>
                ),
            },
            {
                Header: 'Actions',
                accessor: 'actions',
                Cell: ({ row }) => (
                    <Button
                        variant="text   "
                        color="primary"
                        onClick={() => handleSelect(row.original)}
                    >
                        Select
                    </Button>
                ),
            },            
        ],
        []
    );

    const fetchData = useCallback(({ pageSize, pageIndex, globalFilter, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        ValidDataService.getValidTreatmentDataWithLimit(from, pageSize, globalFilter, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal]);

    const handleSelect = (supplier) => {
        onSelect(supplier);
        onClose();
    };

    const handleClose = (event, reason) => {
        // ignore the background click
        if (reason === 'backdropClick') {
            return;
        }
        abortRequest();
        onClose();
    };
    
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>Select treatment</DialogTitle>
            <DialogContent>
                <Box>
                    <Typography variant="h4">Treatments</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <SelectableTable
                        columns={columns}
                        data={state.data}
                        totalRowCount={state.totalRowCount}
                        pageCount={state.pageCount}
                        fetchData={fetchData}
                        loading={state.loading}
                        disableSelection={true}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}